import { addCalendarDays, calendarDayInZone, instantFromWallClock } from '../zonedTime';
import { cents, formatCurrency } from '../money';

/**
 * The AI seam (piece 2 declares it, piece 21 builds it; decision 5380; T2736).
 * One interface, `AiProvider`, with the Anthropic adapter and a scripted fake
 * behind it. Every call through it, and through the enrichment seam beside it,
 * is one `ProviderCallLog` row priced from three Settings rows and counted
 * against one monthly cap.
 *
 * 🔨 Our types in, our types out. No Anthropic field name, status or error code
 * passes the adapter, and an answer is outside data, never an instruction
 * (decision 0202).
 */

/** 🔒 The switch row (decision 1651): `anthropic` or `off`, and an absent row reads `off`. */
export const AI_PROVIDER_KEY = 'ai.provider';
export const ANTHROPIC_PROVIDER = 'anthropic';
/** 🔒 The key, sealed in the KeyProvider envelope the way `ocr.googleApiKey` is. */
export const AI_ANTHROPIC_KEY_KEY = 'ai.anthropicApiKey';
/** 🔒 The model id as Anthropic publishes it, typed by a person; no row is seeded. */
export const AI_MODEL_KEY = 'ai.model';
/** 🔒 The three price rows, each a whole number of cents. */
export const AI_INPUT_CENTS_KEY = 'ai.inputCentsPerMillionTokens';
export const AI_OUTPUT_CENTS_KEY = 'ai.outputCentsPerMillionTokens';
export const AI_WEB_SEARCH_CENTS_KEY = 'ai.webSearchCentsPerSearch';
/** 🔒 The month's ceiling over the whole log, in cents; an absent row reads 5000. */
export const PROVIDER_CALLS_CAP_KEY = 'providerCalls.monthlyCapCents';

export const DEFAULT_AI_INPUT_CENTS = 300;
export const DEFAULT_AI_OUTPUT_CENTS = 1500;
export const DEFAULT_AI_WEB_SEARCH_CENTS = 1;
/** The default is the owner's to settle, open under piece 21's Open issues. */
export const DEFAULT_PROVIDER_CALLS_CAP_CENTS = 5000;

/** 🔒 The three price rows the seed writes (piece 15); the cap ships unset and reads its default. */
export const AI_SETTINGS: readonly { key: string; value: number }[] = [
  { key: AI_INPUT_CENTS_KEY, value: DEFAULT_AI_INPUT_CENTS },
  { key: AI_OUTPUT_CENTS_KEY, value: DEFAULT_AI_OUTPUT_CENTS },
  { key: AI_WEB_SEARCH_CENTS_KEY, value: DEFAULT_AI_WEB_SEARCH_CENTS },
];

/** Every `ai.*` row and the cap, each audited by the one Settings write (decision 5380). */
export const AI_SETTING_KEYS: readonly string[] = [AI_PROVIDER_KEY, AI_ANTHROPIC_KEY_KEY, AI_MODEL_KEY, AI_INPUT_CENTS_KEY, AI_OUTPUT_CENTS_KEY, AI_WEB_SEARCH_CENTS_KEY, PROVIDER_CALLS_CAP_KEY];

/** The rows that hold a whole number of cents, a blank restoring the seed or the default. */
export const AI_CENTS_KEYS: readonly string[] = [AI_INPUT_CENTS_KEY, AI_OUTPUT_CENTS_KEY, AI_WEB_SEARCH_CENTS_KEY, PROVIDER_CALLS_CAP_KEY];

/** The longest model id the row takes; Anthropic's run to a few dozen characters. */
export const AI_MODEL_MAX_LENGTH = 100;

/** The refusal a value of one of the seam's own rows earns, or null; a blank clears the row. */
export function aiSettingProblem(key: string, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (AI_CENTS_KEYS.includes(key)) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 100_000_000) return 'The figure is a whole number of cents, nought or more.';
    return null;
  }
  if (key === AI_MODEL_KEY) {
    if (typeof value !== 'string' || value.trim() === '' || value.trim().length > AI_MODEL_MAX_LENGTH || /\s/.test(value.trim())) return `The model is Anthropic's model id as published, one word of at most ${AI_MODEL_MAX_LENGTH} characters.`;
    return null;
  }
  return null;
}

/**
 * 🔨 What leaves the building, per feature (decision 5380): each feature names
 * the fields its passage lists, and the seam refuses a request carrying a field
 * its feature's list does not name. Test Connection sends no field at all.
 *
 * 🔒 The enrichment seam's search and website reading (decision 5381; T2737):
 * a person search sends the person lookup's three fields, a company search the
 * company lookup's four, and a website reading the record's own domain and the
 * text its pages carried, which is the site's and not ours.
 */
export const AI_FEATURE_FIELDS = {
  testConnection: [],
  personSearch: ['name', 'email', 'companyName'],
  companySearch: ['name', 'website', 'emailDomain', 'postalAddress'],
  websiteReading: ['website', 'pageText'],
  /**
   * 🔒 Ask Claude on the Discovery card (decision 5530; T2780): the Deal Pack's
   * text with its Budget pair, its Target and Maximum and its notes, left out.
   */
  askClaude: ['dealPack'],
  /** 🔒 Make Report on the Discovery card (decision 6003; T2780 batch 2): the same pack, its answered questions inside it. */
  makeReport: ['dealPack'],
} as const satisfies Readonly<Record<string, readonly string[]>>;
export type AiFeature = keyof typeof AI_FEATURE_FIELDS;
export const AI_FEATURES = Object.keys(AI_FEATURE_FIELDS) as AiFeature[];

/** The fields a request names that its feature's list does not, in the order the request names them. */
export function fieldsOutsideFeature(feature: string, fields: Readonly<Record<string, string>>): string[] {
  const allowed: readonly string[] = (AI_FEATURE_FIELDS as Readonly<Record<string, readonly string[]>>)[feature] ?? [];
  return Object.keys(fields).filter((field) => !allowed.includes(field));
}

/** One value of the answer's fixed shape; a value of any other kind is dropped. */
export type AiValueKind = 'text' | 'number' | 'boolean';
export type AiShape = Readonly<Record<string, AiValueKind>>;
export type AiValue = string | number | boolean;

/** 🔒 The plan's four (decision 5380): no key, refused key, rate limited and provider unavailable. */
export const AI_FAILURE_REASONS = ['noKey', 'refusedKey', 'rateLimited', 'providerUnavailable'] as const;
export type AiFailureReason = (typeof AI_FAILURE_REASONS)[number];

/**
 * Why the seam sent nothing or got nothing back: one of the four, the cap
 * reached, a field outside the feature's list, or the seam not bound (switched
 * off, no key or no model).
 */
export type AiRefusal = AiFailureReason | 'capReached' | 'fieldNotListed' | 'inactive';

/** Who made the call: the person whose press it was, or the background job; exactly one is set. */
export type ProviderCaller = { personId: string } | { jobName: string };

/** A request through the seam: its feature, the instruction, the fields it sends, the answer's shape, and the search tool. */
export interface AiRequest {
  feature: AiFeature;
  instruction: string;
  fields: Readonly<Record<string, string>>;
  shape: AiShape;
  webSearch: boolean;
  caller: ProviderCaller;
}

/** 🔒 A value type, not a table: the answer validated into its shape, what the call used, and why nothing came back. */
export interface AiAnswer {
  values: Readonly<Record<string, AiValue>> | null;
  inputTokens: number;
  outputTokens: number;
  searches: number;
  refusal: AiRefusal | null;
}

/** 🔒 What Test Connection reads: the model that answered and its reply, or the refusal. */
export type AiCheck = { ok: true; model: string; reply: string; inputTokens: number; outputTokens: number } | { ok: false; refusal: AiRefusal | 'noModel' };

/** 🔒 The seam (piece 2): `answer`, `check` and `adapterActive`. */
export interface AiProvider {
  answer(request: AiRequest): Promise<AiAnswer>;
  check(caller: ProviderCaller): Promise<AiCheck>;
  adapterActive(): Promise<boolean>;
}

/**
 * Validates an answer's text into its shape (decision 0202): the first JSON
 * object in the text is read, a key the shape names with a value of its kind is
 * kept, and everything else is dropped. Text that holds no object reads as an
 * empty answer.
 */
export function valuesInShape(text: string, shape: AiShape): Record<string, AiValue> {
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start < 0 || end <= start) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(text.slice(start, end + 1));
  } catch {
    // silent: an answer that is not JSON is an answer outside the shape, and it reads as empty (decision 0202)
    return {};
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  const out: Record<string, AiValue> = {};
  for (const [key, kind] of Object.entries(shape)) {
    const value = (parsed as Record<string, unknown>)[key];
    if (kind === 'text' && typeof value === 'string') out[key] = value;
    else if (kind === 'number' && typeof value === 'number' && Number.isFinite(value)) out[key] = value;
    else if (kind === 'boolean' && typeof value === 'boolean') out[key] = value;
  }
  return out;
}

/** The three prices a call is costed at, in cents. */
export interface AiPrices {
  inputCentsPerMillion: number;
  outputCentsPerMillion: number;
  webSearchCents: number;
}

/**
 * 🔒 A call's price in whole cents: the tokens at their per-million prices,
 * rounded up to the next cent, plus each search. 💰 Rounding up makes a call that
 * used any tokens cost at least a cent, so the cap is reached early rather than late.
 */
export function callCostCents(usage: { inputTokens: number; outputTokens: number; searches: number }, prices: AiPrices): number {
  const tokenMillionths = usage.inputTokens * prices.inputCentsPerMillion + usage.outputTokens * prices.outputCentsPerMillion;
  return Math.ceil(tokenMillionths / 1_000_000) + usage.searches * prices.webSearchCents;
}

/** The calendar month an instant falls in, in a zone, as `YYYY-MM`. */
export function monthKeyInZone(instant: Date, zone: string): string {
  return calendarDayInZone(instant, zone).slice(0, 7);
}

/** 🔒 The instants a calendar month starts and ends at in a zone: the first of the month at midnight, and the first of the next. */
export function monthWindowInZone(monthKey: string, zone: string): { start: Date; end: Date } {
  const first = `${monthKey}-01`;
  // The 28th plus four days is always in the next month, whose first is the window's end.
  const next = `${addCalendarDays(`${monthKey}-28`, 4).slice(0, 7)}-01`;
  return { start: instantFromWallClock(`${first}T00:00`, zone), end: instantFromWallClock(`${next}T00:00`, zone) };
}

/** 🔒 The To-Do a refusal raises (decision 5380), at most one open a calendar month. */
export const SPENDING_CAP_KIND = 'SpendingCapReached';
export const PROVIDER_CALL_LOG_SOURCE = 'ProviderCallLog';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;

/** A `YYYY-MM` month as a person reads it, October 2026. */
export function monthYearText(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number) as [number, number];
  return `${MONTHS[month - 1] ?? monthKey} ${year}`;
}

export function spendingCapItemTitle(monthKey: string): string {
  return `The ${monthYearText(monthKey)} spending cap for outside lookups is reached`;
}

/** The item names the month's spend and the cap, and what moves it on. */
export function spendingCapItemBody(monthKey: string, spentCents: number, capCents: number): string {
  return `${monthYearText(monthKey)} has spent ${formatCurrency(cents(spentCents))} on outside lookups against a cap of ${formatCurrency(cents(capCents))}. Every lookup is refused until the month turns or the cap is raised on Settings › Integrations › Anthropic.`;
}

/** 🔒 The To-Do a seam's call ceiling raises, once a month, naming the seam (decision 5460; T2767). */
export function callCeilingItemTitle(seamName: string, monthKey: string): string {
  return `The ${monthYearText(monthKey)} call ceiling for ${seamName} is reached`;
}

export function callCeilingItemBody(seamName: string, monthKey: string, calls: number, ceiling: number): string {
  return `${monthYearText(monthKey)} has made ${calls} ${seamName} lookups against a ceiling of ${ceiling}. Every ${seamName} lookup is refused until the month turns or the ceiling is raised on Settings › Integrations › People Data.`;
}

/** 🔒 The card's own address (decision 5380) and the console page its step 1 links to. */
export const ANTHROPIC_SETUP_ROUTE = '/settings/integrations/anthropic';
export const ANTHROPIC_CONSOLE_KEYS_URL = 'https://console.anthropic.com/settings/keys';

/** The API: the card's reading and its Test Connection. */
export const ANTHROPIC_SETUP_PATH = '/settings/integrations/anthropic';
export const ANTHROPIC_TEST_PATH = '/settings/integrations/anthropic/test';

/** One feature's share of the month's spend. */
export interface ProviderSpendLine {
  feature: string;
  calls: number;
  cents: number;
}

/** 🔒 What the card's step 3 reads beside the rows: this month's spend over both seams, by feature, and the cap in force. */
export interface AnthropicSetupView {
  month: string;
  spentCents: number;
  capCents: number;
  capHeld: boolean;
  byFeature: ProviderSpendLine[];
}

/** Test Connection's answer, with the card's reading after the call so the spend moves with it. */
export interface AnthropicTestAnswer {
  check: AiCheck;
  setup: AnthropicSetupView;
}

/** The longest reply the card shows; the rest is cut, since a reply is outside data shown as plain text. */
export const AI_REPLY_SHOWN_MAX = 80;
